import React from 'react';
import { Link } from 'react-router-dom';
import { PUBLISHING_STAGES_CONFIG } from '@ftt/shared';
import { 
  CheckCircle2, 
  ArrowRight, 
  ShieldCheck, 
  Sparkles, 
  BookOpen, 
  Barcode, 
  Building2, 
  FileCheck, 
  Globe2, 
  ExternalLink,
  Layers,
  HelpCircle,
  Hash,
  Users,
  Briefcase,
  GraduationCap,
  User,
  Info,
  Check
} from 'lucide-react';

export const ProcessPage: React.FC = () => {
  return (
    <div className="space-y-16 py-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 font-sans antialiased text-slate-900">
      
      {/* 1. Header */}
      <section className="text-center space-y-4 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-800 text-[11px] font-bold uppercase tracking-wider font-mono">
          <Sparkles className="w-3.5 h-3.5 text-brand-600" />
          <span>Statutory &amp; Editorial Milestones</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-serif font-black text-slate-900 tracking-tight">
          Publishing Lifecycle &amp; Governance
        </h1>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-medium">
          From initial theological appraisal to statutory Government of India ISBN allotment and national legal deposit, <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong> executes every project with uncompromising rigor.
        </p>
      </section>

      {/* 2. DEDICATED TECHNICAL SECTION: GOVERNMENT OF INDIA ISBN REGISTRATION (isbn.gov.in) */}
      <section className="p-6 sm:p-10 rounded-3xl bg-slate-950 text-white border border-slate-800 shadow-2xl space-y-10">
        
        {/* Header Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-[11px] font-mono uppercase tracking-widest text-brand-400 font-bold">
                Official Statutory Protocol &bull; Government of India
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-100">
              ISBN Allotment &amp; Architecture via Raja Rammohun Roy National Agency
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 max-w-2xl leading-relaxed">
              Administered by the Ministry of Education (Department of Higher Education) via the official statutory portal <a href="https://isbn.gov.in/" target="_blank" rel="noopener noreferrer" className="text-brand-300 font-bold hover:underline">isbn.gov.in</a>.
            </p>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 self-start md:self-auto">
            <a
              href="https://isbn.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-slate-950 text-xs font-bold transition-all inline-flex items-center gap-1.5 shadow-md shadow-brand-500/20"
            >
              <span>isbn.gov.in Portal</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* 2A. ANATOMY OF A 13-DIGIT INDIAN ISBN BARCODE */}
        <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800/90 shadow-xl space-y-6">
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800/80 pb-4">
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400 font-bold block">
                Technical Barcode Anatomy &bull; ISO 2108 Specification
              </span>
              <h3 className="font-serif font-bold text-lg sm:text-xl text-white">
                Anatomy of a 13-Digit Indian ISBN
              </h3>
            </div>
            <span className="text-xs text-slate-400 font-mono bg-slate-800/80 px-3 py-1 rounded-full self-start sm:self-auto border border-slate-700">
              Structure: 5 Distinct Elements
            </span>
          </div>

          {/* Interactive Barcode Simulator Display */}
          <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800/80 text-center space-y-4">
            
            {/* Visual Simulated Barcode Lines */}
            <div className="inline-block p-4 sm:p-6 bg-white rounded-xl shadow-lg border border-slate-200 text-slate-950">
              <span className="text-[11px] font-mono uppercase tracking-widest text-slate-600 font-bold block pb-2">
                International Standard Book Number
              </span>
              
              {/* CSS Vector Barcode Representation */}
              <div className="h-16 sm:h-20 flex items-center justify-center gap-[2px] sm:gap-[3px] px-2 py-1 bg-white">
                {[4,2,3,1,4,2,1,3,2,4,1,2,3,4,1,2,4,2,3,1,2,4,3,1,2,3,1,4,2,3,1,2,4,1,3,2,4,2,1,3,4,2,1,3,2,4].map((width, i) => (
                  <div
                    key={i}
                    style={{ width: `${width}px` }}
                    className={`h-full ${i % 2 === 0 ? 'bg-slate-900' : 'bg-transparent'} ${i === 0 || i === 1 || i === 22 || i === 23 || i === 44 || i === 45 ? 'h-[110%] bg-slate-950' : ''}`}
                  />
                ))}
              </div>

              {/* Hyphenated 13-Digit Breakdown Display */}
              <div className="pt-3 flex items-center justify-center flex-wrap gap-1 sm:gap-2 font-mono font-black text-base sm:text-2xl tracking-wider">
                <span className="text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">978</span>
                <span className="text-slate-400">-</span>
                <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">81</span>
                <span className="text-slate-400">-</span>
                <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">959959</span>
                <span className="text-slate-400">-</span>
                <span className="text-purple-600 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">7</span>
                <span className="text-slate-400">-</span>
                <span className="text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">1</span>
              </div>
            </div>

            {/* 5 Element Callout Badges Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-2 text-left">
              
              <div className="p-3.5 rounded-xl bg-slate-900 border border-amber-500/30 space-y-1">
                <div className="flex items-center gap-1.5 text-amber-400 font-mono font-bold text-xs">
                  <span className="w-5 h-5 rounded bg-amber-500/20 flex items-center justify-center text-[10px]">1</span>
                  <span>GS1 Element</span>
                </div>
                <div className="font-mono text-sm font-bold text-white">978 / 979</div>
                <p className="text-[11px] text-slate-400 leading-snug">
                  Identifies the global book publishing industry in the international GS1 barcode registry ("Bookland").
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-900 border border-blue-500/30 space-y-1">
                <div className="flex items-center gap-1.5 text-blue-400 font-mono font-bold text-xs">
                  <span className="w-5 h-5 rounded bg-blue-500/20 flex items-center justify-center text-[10px]">2</span>
                  <span>Registration Group</span>
                </div>
                <div className="font-mono text-sm font-bold text-white">81 or 93 (India)</div>
                <p className="text-[11px] text-slate-400 leading-snug">
                  Geographical and linguistic code assigned exclusively to India under the international ISO 2108 standard.
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-900 border border-emerald-500/30 space-y-1">
                <div className="flex items-center gap-1.5 text-emerald-400 font-mono font-bold text-xs">
                  <span className="w-5 h-5 rounded bg-emerald-500/20 flex items-center justify-center text-[10px]">3</span>
                  <span>Registrant Element</span>
                </div>
                <div className="font-mono text-sm font-bold text-white">Publisher Prefix</div>
                <p className="text-[11px] text-slate-400 leading-snug">
                  Unique corporate identifier allotted by RRRNA to <em>For The Truth Press Private Limited</em>.
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-900 border border-purple-500/30 space-y-1">
                <div className="flex items-center gap-1.5 text-purple-400 font-mono font-bold text-xs">
                  <span className="w-5 h-5 rounded bg-purple-500/20 flex items-center justify-center text-[10px]">4</span>
                  <span>Publication Element</span>
                </div>
                <div className="font-mono text-sm font-bold text-white">Title &amp; Format ID</div>
                <p className="text-[11px] text-slate-400 leading-snug">
                  Identifies the specific title, edition, language translation, or format (Hardcover vs. Paperback).
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-900 border border-rose-500/30 space-y-1">
                <div className="flex items-center gap-1.5 text-rose-400 font-mono font-bold text-xs">
                  <span className="w-5 h-5 rounded bg-rose-500/20 flex items-center justify-center text-[10px]">5</span>
                  <span>Check Digit</span>
                </div>
                <div className="font-mono text-sm font-bold text-white">Mathematical Verify</div>
                <p className="text-[11px] text-slate-400 leading-snug">
                  Calculated with Modulo 10 (alternating weights 1 &amp; 3) to guarantee 100% optical scanner accuracy.
                </p>
              </div>

            </div>

          </div>
        </div>

        {/* 2B. WHICH ISBN CATEGORY FITS YOU? (GOVT OF INDIA CLASSIFICATION) */}
        <div className="space-y-4">
          <div className="border-b border-slate-800 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-brand-400 font-bold block">
                Official Applicant Categorization &bull; isbn.gov.in
              </span>
              <h3 className="font-serif font-bold text-lg sm:text-xl text-white">
                Which ISBN Category Fits You?
              </h3>
            </div>
            <span className="text-xs text-slate-400">
              Government of India classifies all applicants into 5 distinct categories
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            
            {/* Category 1 */}
            <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-rose-400 font-bold">
                  <User className="w-4 h-4" />
                  <span className="text-sm">Author (Self-publisher)</span>
                </div>
                <span className="text-[10px] font-mono text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
                  Single ISBN
                </span>
              </div>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                For individual authors or editors based in India who publish their own literary or academic work. Eligible for a single ISBN per application upon proof of authorship.
              </p>
            </div>

            {/* Category 2 */}
            <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-purple-400 font-bold">
                  <GraduationCap className="w-4 h-4" />
                  <span className="text-sm">Institutional Author</span>
                </div>
                <span className="text-[10px] font-mono text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
                  Single ISBN
                </span>
              </div>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                For institutions (universities, colleges, government bodies, trusts, NGOs) based in India involved in publishing conference or seminar proceedings or standalone monographs.
              </p>
            </div>

            {/* Category 3 */}
            <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-blue-400 font-bold">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Individual Publisher</span>
                </div>
                <span className="text-[10px] font-mono text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
                  Single ISBN
                </span>
              </div>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                For non-commercial publishers based in India who publish in a personal capacity without formal commercial company incorporation. Eligible for a single ISBN per application.
              </p>
            </div>

            {/* Category 4 */}
            <div className="p-5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-cyan-400 font-bold">
                  <Building2 className="w-4 h-4" />
                  <span className="text-sm">Institutional Publisher</span>
                </div>
                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950/80 border border-emerald-800/40 px-2 py-0.5 rounded font-bold">
                  Block of ISBNs
                </span>
              </div>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                For registered institutions (universities, colleges, research organizations, trusts, NGOs) with ongoing publishing programs. Eligible for a block allotment of ISBNs.
              </p>
            </div>

            {/* Category 5 - FOR THE TRUTH PRESS HIGHLIGHTED */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-brand-950/90 via-slate-900 to-amber-950/50 border-2 border-brand-500/60 shadow-lg space-y-2.5 md:col-span-2 lg:col-span-2 relative overflow-hidden">
              <div className="absolute top-2 right-2">
                <span className="px-2.5 py-1 rounded-full bg-brand-500 text-slate-950 font-mono font-bold text-[10px] uppercase tracking-wider">
                  FTT Press Classification
                </span>
              </div>
              <div className="flex items-center gap-2 text-amber-300 font-bold">
                <Briefcase className="w-4 h-4 text-amber-400" />
                <span className="text-base font-serif">Commercial Publisher</span>
              </div>
              <p className="text-slate-200 leading-relaxed text-xs">
                For registered businesses or incorporated companies in India publishing books for commercial distribution and sale. As an incorporated private limited press (CIN registered), <strong>FOR THE TRUTH PRESS PRIVATE LIMITED</strong> operates under this status, eligible for <strong>Block ISBN Allotments</strong> to govern sustained publishing and vernacular translation pipelines.
              </p>
              <div className="flex items-center gap-3 pt-1 text-[11px] text-amber-300/90 font-mono font-medium">
                <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400" /> Corporate CIN Verification</span>
                <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400" /> Block Allotment Tier</span>
              </div>
            </div>

          </div>
        </div>

        {/* 2C. 4-STAGE PORTAL WORKFLOW (HOW TO OBTAIN ISBN) */}
        <div className="space-y-4">
          <div className="border-b border-slate-800 pb-3">
            <span className="text-[10px] font-mono uppercase tracking-widest text-brand-400 font-bold block">
              Official Workflow &bull; Department of Higher Education
            </span>
            <h3 className="font-serif font-bold text-lg sm:text-xl text-white">
              How to Obtain an ISBN in India (The 4-Stage Lifecycle)
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            
            <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-3 relative">
              <div className="flex items-center justify-between text-brand-400 font-mono font-bold">
                <span className="w-7 h-7 rounded-lg bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-xs text-brand-300">01</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">Initial Setup</span>
              </div>
              <h4 className="text-sm font-bold text-white">Portal Registration</h4>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                One-time account creation on <strong className="text-white">isbn.gov.in</strong>. Corporate KYC submission including Certificate of Incorporation (CIN), PAN, registered address proof, and authorized signatory board resolution.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-3 relative">
              <div className="flex items-center justify-between text-brand-400 font-mono font-bold">
                <span className="w-7 h-7 rounded-lg bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-xs text-brand-300">02</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">Formal Filing</span>
              </div>
              <h4 className="text-sm font-bold text-white">Application Submission</h4>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                Secure login to the dashboard and submission of the formal ISBN requisition. Selection of publisher category (Commercial Publisher) and declaration of proposed annual publication pipeline.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-3 relative">
              <div className="flex items-center justify-between text-brand-400 font-mono font-bold">
                <span className="w-7 h-7 rounded-lg bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-xs text-brand-300">03</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">Bibliographic Entry</span>
              </div>
              <h4 className="text-sm font-bold text-white">Book Details Upload</h4>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                Inputting exact bibliographic data: Title, Subtitle, Author, Translator, Language, Physical Trim Size, Page Count, Binding Format (Hardcover / PB), Dewey Decimal (DDC) &amp; BISAC classifications, and Title Page / Verso Copyright page proofs.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-3 relative">
              <div className="flex items-center justify-between text-brand-400 font-mono font-bold">
                <span className="w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-xs text-emerald-300">04</span>
                <span className="text-[10px] uppercase tracking-wider text-emerald-400">Statutory Release</span>
              </div>
              <h4 className="text-sm font-bold text-white">ISBN Issuance</h4>
              <p className="text-slate-300 leading-relaxed text-[11px]">
                Official review and administrative approval by RRRNA officers. Permanent 13-digit identifier assigned, entry in the Indian National Bibliography, and vector EAN-13 barcode generated for pre-press printing.
              </p>
            </div>

          </div>
        </div>

        {/* 2D. 6 CORE TENETS: KNOW IT, USE IT, THRIVE WITH IT! */}
        <div className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-4">
          <div className="border-b border-slate-800 pb-3">
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400 font-bold block">
              Statutory Principles &bull; Ministry of Education Guidelines
            </span>
            <h3 className="font-serif font-bold text-base sm:text-lg text-white">
              6 Core Tenets of the Indian ISBN System
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 text-xs">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="w-6 h-6 rounded-lg bg-amber-500/20 text-amber-400 font-bold text-xs flex items-center justify-center shrink-0">1</div>
              <div>
                <strong className="text-white block font-sans">Unique Identification</strong>
                <span className="text-[11px] text-slate-400">Machine-readable 13-digit code establishing global exclusivity for every published monograph.</span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="w-6 h-6 rounded-lg bg-amber-500/20 text-amber-400 font-bold text-xs flex items-center justify-center shrink-0">2</div>
              <div>
                <strong className="text-white block font-sans">Enhance Discoverability</strong>
                <span className="text-[11px] text-slate-400">Enables international retailers, academic libraries, and wholesalers to index and procure publications.</span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-400 font-bold text-xs flex items-center justify-center shrink-0">3</div>
              <div>
                <strong className="text-emerald-300 block font-sans">ISBN is Free in India</strong>
                <span className="text-[11px] text-slate-400">Unlike foreign agencies that charge steep commercial fees, RRRNA issues official ISBNs in India completely free of cost.</span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="w-6 h-6 rounded-lg bg-amber-500/20 text-amber-400 font-bold text-xs flex items-center justify-center shrink-0">4</div>
              <div>
                <strong className="text-white block font-sans">Streamline Sales &amp; POS</strong>
                <span className="text-[11px] text-slate-400">Enables automated point-of-sale optical scanning, inventory tracking, and warehouse distribution.</span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="w-6 h-6 rounded-lg bg-amber-500/20 text-amber-400 font-bold text-xs flex items-center justify-center shrink-0">5</div>
              <div>
                <strong className="text-white block font-sans">Open to All Publishers</strong>
                <span className="text-[11px] text-slate-400">Available transparently to self-publishing authors, university faculties, NGOs, and incorporated commercial presses.</span>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-950 border border-slate-800">
              <div className="w-6 h-6 rounded-lg bg-blue-500/20 text-blue-400 font-bold text-xs flex items-center justify-center shrink-0">6</div>
              <div>
                <strong className="text-blue-300 block font-sans">Indian Code: 81 / 93</strong>
                <span className="text-[11px] text-slate-400">The second element in the ISBN structure identifies India's sovereign publication territory worldwide.</span>
              </div>
            </div>
          </div>
        </div>

        {/* 2E. MANDATORY LEGAL DEPOSIT BOX */}
        <div className="p-5 rounded-2xl bg-slate-900 border border-brand-500/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-brand-300 font-mono font-bold text-xs">
              <ShieldCheck className="w-4 h-4 text-brand-400" />
              <span>Statutory Compliance: Delivery of Books (Public Libraries) Act, 1954</span>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed max-w-2xl">
              Within 30 days of release, physical statutory copies of every title are delivered to the <strong>National Library of India (Kolkata)</strong> and designated repository libraries in New Delhi, Mumbai, and Chennai for permanent preservation in the Indian National Bibliography.
            </p>
          </div>
          <span className="shrink-0 px-3 py-1.5 rounded-lg bg-brand-950 text-brand-300 font-mono text-[10px] font-bold border border-brand-700/50">
            Mandatory Statutory Filing
          </span>
        </div>

      </section>

      {/* 3. 12-Stage Timeline Steps */}
      <section className="space-y-4">
        <div className="text-center space-y-1 pb-4">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Editorial Progression
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            The 12-Stage Editorial Pathway
          </h2>
        </div>

        <div className="space-y-6 relative before:absolute before:inset-y-0 before:left-6 md:before:left-1/2 before:w-0.5 before:bg-slate-200">
          {PUBLISHING_STAGES_CONFIG.map((stage, idx) => (
            <div
              key={stage.status}
              className={`relative flex flex-col md:flex-row gap-6 items-start ${
                idx % 2 === 0 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Center Marker */}
              <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white border-4 border-brand-500 flex items-center justify-center font-mono font-bold text-xs text-brand-900 shadow-md z-10">
                {stage.order}
              </div>

              {/* Content Card */}
              <div className="ml-16 md:ml-0 md:w-[calc(50%-2.5rem)] p-6 rounded-2xl bg-white border border-slate-200/90 shadow-2xs hover:border-brand-300 transition-all space-y-2">
                <div className="flex items-center justify-between text-xs font-mono text-brand-700 font-bold uppercase">
                  <span>Phase {Math.ceil(stage.order / 3)}</span>
                  <span>Stage {stage.order} of 12</span>
                </div>
                <h3 className="font-serif text-base font-bold text-slate-900">
                  {stage.label}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-sans">
                  {stage.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Author / Rights Inquiries CTA */}
      <section className="p-8 sm:p-12 rounded-3xl bg-slate-950 text-white text-center space-y-6 border border-slate-800">
        <div className="max-w-2xl mx-auto space-y-3">
          <span className="text-[11px] font-mono uppercase tracking-widest text-brand-400 font-bold">
            Publishing Partnerships &amp; Acquisitions
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-100">
            Submit a Proposal to FOR THE TRUTH PRESS
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 font-light max-w-lg mx-auto leading-relaxed">
            We partner with authors, rights holders, and academic seminaries to bring theological conviction and vernacular literature to press.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <Link
            to="/submit-enquiry"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-xs uppercase tracking-wider shadow-md transition-all"
          >
            <span>Begin Publishing Enquiry</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/publications"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700 font-semibold text-xs transition-all"
          >
            <span>Explore 432 Production Records</span>
          </Link>
        </div>
      </section>

    </div>
  );
};
